import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Spinner } from '@Components/Spinner/Spinner';
import {
  ConnectFailure,
  ConnectFailureType,
  getConsentInfo,
  submitConsent,
  TxExpiredError
} from '@src/utils/auth/oauth-client';
import { useSetError } from '@src/redux/modals';
import { ConsentDecision, ConsentDecisionType, ConnectConsentView } from './ConnectConsentView';
import { CONSENT_LOADING_LABEL } from './connect-constants';

interface ConnectConsentProps {
  tx: string;
  onFailure: (failure: ConnectFailureType) => void;
}

// Loads what the user is being asked to approve, posts the decision, and leaves the page for the
// harness redirect the backend returns. A remembered approval skips the screen and posts allow.
export const ConnectConsent = ({ tx, onFailure }: ConnectConsentProps): ReactNode => {
  const setError = useSetError();
  const [decision, setDecision] = useState<ConsentDecisionType>();
  // A remembered approval that failed to send falls back to the consent screen.
  const [autoFailed, setAutoFailed] = useState(false);
  const autoSubmitted = useRef(false);

  const { data: info, error: loadError } = useQuery({
    queryKey: ['connectConsent', tx],
    queryFn: () => getConsentInfo(tx),
    // An expired transaction doesn't recover on retry, and this page shows its own errors.
    retry: false,
    meta: { suppressGlobalError: true }
  });

  const { mutate: sendDecision } = useMutation({
    mutationFn: (allow: boolean) => submitConsent(tx, allow),
    // Leaves the page; the buttons stay disabled until the browser navigates. replace() keeps the
    // decided sign-in out of history, so Back can't reopen its consent screen.
    onSuccess: ({ redirect }) => window.location.replace(redirect),
    onError: e => {
      setDecision(undefined);
      setAutoFailed(true);
      if (e instanceof TxExpiredError) {
        onFailure(ConnectFailure.EXPIRED);
        return;
      }
      setError({ message: `Couldn't complete the connection: ${e.message}`, reportError: e });
    },
    meta: { suppressGlobalError: true }
  });

  const decide = useCallback(
    (next: ConsentDecisionType) => {
      setDecision(next);
      sendDecision(next === ConsentDecision.ALLOW);
    },
    [sendDecision]
  );

  useEffect(() => {
    if (!loadError) return;
    if (loadError instanceof TxExpiredError) {
      onFailure(ConnectFailure.EXPIRED);
      return;
    }
    onFailure(ConnectFailure.SIGNIN_FAILED);
  }, [loadError, onFailure]);

  useEffect(() => {
    if (!info?.remembered || autoSubmitted.current) return;
    autoSubmitted.current = true;
    decide(ConsentDecision.ALLOW);
  }, [info, decide]);

  if (!info || (info.remembered && !autoFailed)) {
    return (
      <div
        className='bpm-login connect-page'
        role='status'
        aria-label={CONSENT_LOADING_LABEL}
        data-testid='connect-loading'
      >
        <div className='bpm-login-inner'>
          <div className='login-form'>
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <ConnectConsentView
      info={info}
      submitting={decision}
      onAllow={() => decide(ConsentDecision.ALLOW)}
      onCancel={() => decide(ConsentDecision.CANCEL)}
    />
  );
};
